/**
 * Tick use case (contracts.md, ADR-7, ADR-8).
 * speed=0 → render only. effectiveDelta = deltaMs × speed.
 * Banishment instead of damageOrb. Defeat: mana ≤ 0.
 */

import type { RenderPort, AudioPort } from "../ports.js";
import { sessionToView } from "../state-view.js";
import {
  addMana,
  banishMonster,
  spawnNextInWave,
  tickMonsterMovement,
  tickTowerShooting,
  tickShots,
  tickHitFlashes,
  tickShrines,
  setWon,
} from "../../domain/session.js";
import { tickMonsterDots } from "../../domain/gem-effects.js";
import { stageBaseXp } from "../../domain/mage-xp.js";
import { getXpMultiplier } from "../../domain/stage-settings.js";

// AS3-faithful (IngameInitializer.manaChargePerFrame): 0.5/30 ≈ 0.0167 per frame at 30 fps = 0.5 mana/sec = 0.0005/ms.
const MANA_REGEN_PER_MS = 0.0005;

export interface TickDeps {
  render: RenderPort;
  audio: AudioPort;
  getSession(): import("../../domain/session.js").GameSession | null;
  onWin?: (fieldId: string) => void;
  /** Called exactly once when a field is won. Receives fieldId and earnedXp for XP award. */
  onStageWon?: (fieldId: string, earnedXp: number) => void;
  /** Accumulate kill count. */
  incrementKills?: (n: number) => void;
  /** Accumulate banish count. */
  incrementBanishes?: (n: number) => void;
  /** Accumulate real-time played (ms). */
  addPlayTime?: (ms: number) => void;
  /** Called exactly once when stage ends (win or lose). */
  onStageEnd?: (fieldId: string, won: boolean) => void;
}

export function createTick(deps: TickDeps) {
  let playedLoseSound = false;
  let stageEndFired = false;
  return function tick(deltaMs: number): void {
    const session = deps.getSession();
    if (!session) return;

    if (session.won === null) {
      playedLoseSound = false;
      stageEndFired = false;
    }

    // Won/lost — render only (+ one-shot lose sound + one-shot onStageEnd)
    if (session.won !== null) {
      if (session.won === false && !playedLoseSound) {
        deps.audio.play("lose");
        playedLoseSound = true;
      }
      if (!stageEndFired) {
        stageEndFired = true;
        deps.onStageEnd?.(session.field.id, session.won);
      }
      deps.render.update(sessionToView(session, "game"));
      return;
    }

    // ADR-8: frozen (prep phase or pause) — render only
    if (session.speed === 0) {
      deps.render.update(sessionToView(session, "game"));
      return;
    }

    // Accumulate real wall-clock time (deltaMs, not effectiveDelta)
    if (deltaMs > 0) {
      deps.addPlayTime?.(deltaMs);
    }

    const effectiveDelta = deltaMs * session.speed;

    session.elapsedMs += effectiveDelta;
    addMana(session, effectiveDelta * MANA_REGEN_PER_MS * (1 + 0.01 * session.skillEffects.manaReplenish));

    while (spawnNextInWave(session, session.elapsedMs)) {
      /* spawn until no more this tick */
    }

    const reachedOrb = tickMonsterMovement(session, effectiveDelta);
    tickMonsterDots(session, effectiveDelta);
    const monstersBeforeShots = session.monsters.length;
    const { kills: shotKills } = tickShots(session, effectiveDelta);
    const { shots } = tickTowerShooting(session, effectiveDelta);
    tickShrines(session, effectiveDelta);
    tickHitFlashes(session);
    // Some kills come from splash/chain/poison (not counted in shotKills).
    const totalKills = Math.max(shotKills, monstersBeforeShots - session.monsters.length);

    if (totalKills > 0) {
      deps.incrementKills?.(totalKills);
    }

    for (let i = 0; i < shots; i++) {
      deps.audio.play("shot");
    }
    for (let i = 0; i < totalKills; i++) {
      deps.audio.play("monsterDeath");
    }

    // ADR-7: banishment instead of damageOrb; monsters respawn at start with current HP
    for (const m of reachedOrb) {
      banishMonster(session, m);
    }
    if (reachedOrb.length > 0) {
      deps.incrementBanishes?.(reachedOrb.length);
      deps.audio.play("orbHit");
    }

    // Defeat: mana ≤ 0
    if (session.mana.current <= 0) {
      setWon(session, false);
    }

    // Victory: all waves done, no monsters, mana > 0
    if (
      session.won === null &&
      session.currentWaveIndex >= session.waves.length &&
      session.monsters.length === 0 &&
      session.mana.current > 0
    ) {
      setWon(session, true);
      deps.audio.play("win");
      const baseXp = stageBaseXp(session.originalWavesNum, session.originalMonsterBaseHp);
      const earnedXp = Math.round(baseXp * getXpMultiplier(session.stageSettings));
      deps.onStageWon?.(session.field.id, earnedXp);
      deps.onWin?.(session.field.id);
    }

    // Fire onStageEnd once when won transitions from null to a value
    if (session.won !== null && !stageEndFired) {
      stageEndFired = true;
      deps.onStageEnd?.(session.field.id, session.won);
    }

    deps.render.update(sessionToView(session, "game"));
  };
}
